'use client';

import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  LinearProgress,
  Typography,
} from '@mui/material';
import type { PagerData } from '@ocode/domain';
import { type Project, type ProjectBreedKey } from '@ocode/domain';
import { ApiRequestContext } from '@ocodelib/api-common';
import { FlexColumn, FlexRow, TopAbsolute } from '@ocodelib/mui';
import { useDebounceEffect, useLoadingVisible } from '@ocodelib/mui/hooks';
import { BbsPagination } from '@repo-foundation/components/BbsPagination';
import { CustomDialogTitle } from '@repo-foundation/components/CustomDialogTitle';
import { useCallback, useState } from 'react';
import { toast } from 'react-toastify';
import { BreedChips } from '../../components/BreedChips';
import { CustomDialog } from '../../components/CustomDialog';
import { NoDataView } from '../../components/NoDataView';
import { ProjectListRow } from '../../components/ProjectListRow';
import { useApi } from '../../provider/useApi';
import { handleError } from '../../util/handle-error';

interface SearchParams {
  rowsPerPage: number;
  pageNumber: number;
  title?: string;
}

const DEAFULT_SEARCH_PARAM: SearchParams = {
  rowsPerPage: 5,
  pageNumber: 0,
};

export interface ProjectPickerDialogProps {
  open: boolean;
  breed?: ProjectBreedKey;
  disableBreedSelect?: boolean;
  title?: string;
  onClose: () => void;
  onSelected: (project: Project) => void;
}

export function ProjectPickerDialog(props: ProjectPickerDialogProps) {
  const { open, onClose, disableBreedSelect = false, title = '프로젝트 선택', onSelected } = props;
  const api = useApi();
  const [searchParams, setSearchParams] = useState<SearchParams>(DEAFULT_SEARCH_PARAM);
  const [loading, setLoading] = useState(false);
  const loadingVisible = useLoadingVisible(loading);
  const [pagerData, setPagerData] = useState<PagerData<Project>>();
  const [refreshToken, setRefreshToken] = useState(0);
  const [breed, setBreed] = useState<ProjectBreedKey | undefined>(() => props.breed);

  const updateSearchParams = (part: Partial<SearchParams>) => {
    setSearchParams((p) => ({ ...p, ...part }));
  };

  const handleClose = () => {
    onClose();
  };

  const doDelete = useCallback(
    async (projectId: string): Promise<boolean> => {
      try {
        setLoading(true);
        await api.project.delete({ projectId });
        return true;
      } catch (err) {
        handleError(err);
      } finally {
        setLoading(false);
      }
      return false;
    },
    [api],
  );

  const doLoad = useCallback(
    async (ctx: ApiRequestContext, searchOptions: SearchParams, breed?: ProjectBreedKey) => {
      try {
        setLoading(true);
        const { pagerData } = await api.project.list({
          ctx,
          ...searchOptions,
          breed: breed ? [breed] : undefined,
        });
        if (ctx.canceled) return;
        setPagerData(pagerData);
      } catch (err) {
        handleError(err);
      } finally {
        setLoading(false);
      }
    },
    [api],
  );

  useDebounceEffect(
    () => {
      const ctx = ApiRequestContext.of();
      doLoad(ctx, searchParams, breed);
      return () => {
        ctx.cancel();
      };
    },
    [doLoad, searchParams, breed, refreshToken],
    { wait: 200 },
  );

  const handleClickRow = (item: Project) => () => {
    onSelected?.(item);
  };

  const handleClickDeleteBtn = (item: Project) => () => {
    const msg = `[${item.projectName}] 프로젝트를 삭제하시겠습니까?`;
    if (!confirm(msg)) return;
    doDelete(item.projectId).then((succ) => {
      if (succ) {
        toast.success('삭제되었습니다');
        setRefreshToken(Date.now());
        return;
      }
    });
  };

  // 페이지 번호 클릭
  const handleClickPageNumber = (pageNumber: number) => {
    updateSearchParams({ pageNumber });
    window.scrollTo({ top: 0, behavior: 'instant' });
  };

  const {
    totalPages = 0,
    pageNumber = 0,
    elements = [],
    offset = 0,
    totalElements = 0,
  } = pagerData ?? {};

  const itemNumMax = totalElements - offset;

  return (
    <CustomDialog
      className="ProjectPickerDialog-root"
      open={open}
      maxWidth="sm"
      fullWidth
      onClose={handleClose}
    >
      {disableBreedSelect && <CustomDialogTitle title={title} onClose={handleClose} />}
      {!disableBreedSelect && (
        <CustomDialogTitle onClose={handleClose}>
          <Box mt={0.5}>
            <Typography variant="body2" sx={{ color: '#000' }}>
              {title}
            </Typography>
            <BreedChips
              currentBreed={breed}
              breed="all"
              onChange={setBreed}
              undefinedLabel="전체"
              sx={{
                justifyContent: 'flex-start',
                pb: 1,
              }}
            />
          </Box>
        </CustomDialogTitle>
      )}

      <DialogContent
        dividers
        sx={{
          py: 1,
          px: 1,
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
        }}
      >
        <FlexColumn>
          {totalElements > 0 && (
            <Typography
              variant="body2"
              sx={{
                pl: 2,
                color: '#888',
              }}
            >
              전체 {totalElements}개
            </Typography>
          )}

          {elements.length > 0 && (
            <Box
              sx={{
                flex: 1,
                '& .ProjectListRow-root': {
                  '& + .ProjectListRow-root': {
                    borderTop: '1px solid #ddd',
                  },
                },
              }}
            >
              {elements.map((elem, i) => (
                <ProjectListRow
                  key={elem.projectId}
                  project={elem}
                  seq={itemNumMax - i}
                  onClick={handleClickRow(elem)}
                  onClickDelete={handleClickDeleteBtn(elem)}
                />
              ))}
            </Box>
          )}

          <NoDataView
            visible={!loading && elements.length === 0}
            title="등록된 프로젝트가 없습니다"
          />
        </FlexColumn>
        {loadingVisible && (
          <TopAbsolute>
            <LinearProgress color="secondary" />
          </TopAbsolute>
        )}
      </DialogContent>
      <DialogActions sx={{ display: 'block' }}>
        <Box>
          <BbsPagination
            page={pageNumber}
            count={totalPages}
            onChange={handleClickPageNumber}
            disabled={loading}
          />
        </Box>
        <FlexRow.End alignItems="center">
          <Button onClick={handleClose}>닫기</Button>
        </FlexRow.End>
      </DialogActions>
    </CustomDialog>
  );
}
